class CartMilestones extends HTMLElement {
  constructor() {
    super();

    // Clone the milestones required for this element.
    this.milestones = JSON.parse(
      JSON.stringify(window.cartMilestones.milestones)
    );
    this.finalMessage = window.cartMilestones.finalMessage;

    if (!this.milestones.length) {
      return;
    }

    // Sort milestones by ascending threshold requirement.
    this.milestones.sort((a, b) => a.threshold - b.threshold);

    // A "dummy" first milestone is always needed for piecewise calculation.
    this.milestones.unshift({
      threshold: 0,
      position: 0,
      label: '',
      heading: ''
    });

    // Calculate each milestone's position now that division by zero is not a risk.
    this.milestones.forEach((milestone, index) => {
      // Milestones are equally spaced on the progress bar.
      milestone.position = (index / (this.milestones.length - 1)) * 100;
    });

    this.lastMilestone = this.milestones[this.milestones.length - 1];

    $(document).on('Cart:set', this.updateProgress.bind(this));
  }

  connectedCallback() {
    if (this.milestones.length <= 1) {
      this.remove();
      return;
    }

    this.style.setProperty('--progress', '0%');
    this.style.setProperty(
      '--label-width',
      `${(1 / (this.milestones.length - 1)) * 100}%`
    );

    this.innerHTML = `
      <div class="cart-milestones__heading caption"></div>
      <div class="cart-milestones__bar">
        <div class="cart-milestones__progress"></div>
        <div class="cart-milestones__svg-container">
          ${this.drawProgressBar()}
        </div>
        ${this.milestones.map((milestone) => this.renderMilestoneLabel(milestone)).join('')}
      </div>
    `;

    const svgContainer = this.querySelector('.cart-milestones__svg-container');
    const resizeObserver = new ResizeObserver(() => {
      svgContainer.innerHTML = this.drawProgressBar();
    });

    this.updateProgress();

    resizeObserver.observe(svgContainer);
  }

  /**
   * Generate the SVG markup for this progress bar and its milestones.
   * @returns {string} - The template string for this SVG element.
   */
  drawProgressBar() {
    const markerRadius = 4.5;
    const barHeight = 3;

    return `
      <svg class="mask" width="100%" height="32">
        <defs>
          <mask id="cart-milestones-bar-mask" x="0" y="0" width="100%" height="100%">
            <rect
              class="cart-milestones__rect-mask"
              x="0"
              y="0"
              width="100%"
              height="100%"
            />
            <rect
              x="${markerRadius}"
              y="${markerRadius + barHeight / 2}"
              width="100%"
              height="${barHeight}"
              rx="${barHeight}"
            />
            ${this.milestones.map((milestone) => this.renderMilestoneMarker(milestone, markerRadius))}
          </mask>
        </defs>
        <rect
          class="cart-milestones__rect-mask"
          x="0"
          y="0"
          width="100%"
          height="100%"
          mask="url(#cart-milestones-bar-mask)"
        />
      </svg>
    `;
  }

  /**
   * Returns a template string for a circle element, representing a milestone marker.
   * @param {Object} milestone - The milestone object to render a marker for.
   * @param {float} radius - The radius for the progress marker.
   * @returns {string} - The template string for this SVG element.
   */
  renderMilestoneMarker(milestone, radius) {
    // Ensure markers stay completely visible.
    const minPosition =
      milestone.position === 0 ? `${radius}px` : `${milestone.position}%`;
    const styles = `
      --max: calc(100% - ${radius}px);
      cx: min(var(--max), ${minPosition});
    `;
    return `
      <circle
        style="${styles}"
        class="cart-milestones__marker"
        cy="${3 + radius}"
        r="${radius}"
      />
    `;
  }

  /**
   * Returns a template string for an absolutely positioned milestone label.
   * @param {Object} milestone - The custom milestone object to display the label for.
   * @returns {string} - The template string for this element.
   */
  renderMilestoneLabel(milestone) {
    return `
      <div
        class="cart-milestones__label caption"
        style="--position: ${milestone.position}%;"
      >
        ${milestone.label}
      </div>
    `;
  }

  /**
   * Update the progress bar based on the cart's final subtotal.
   */
  updateProgress() {
    const totalPrice = window.theme.cart.content.total_price;
    const { progress, nextMilestone, followingMilestone } =
      this.calculateProgress(totalPrice);
    const message = this.generateMessage(
      totalPrice,
      nextMilestone,
      followingMilestone
    );

    this.updateUI(progress, message);
  }

  /**
   * Calculates progress and determines the next milestones.
   * @param {number} totalPrice
   * @returns {Object} { progress: number, nextMilestone: Object|null, followingMilestone: Object|null }
   */
  calculateProgress(totalPrice) {
    if (totalPrice >= this.lastMilestone.threshold) {
      return {
        progress: this.lastMilestone.position,
        nextMilestone: null,
        followingMilestone: null
      };
    }

    for (let i = 0; i < this.milestones.length - 1; i++) {
      const milestone = this.milestones[i];
      const nextMilestone = this.milestones[i + 1];

      if (
        totalPrice >= milestone.threshold &&
        totalPrice < nextMilestone.threshold
      ) {
        const segmentProgress =
          (totalPrice - milestone.threshold) /
          (nextMilestone.threshold - milestone.threshold);
        const progress =
          milestone.position +
          segmentProgress * (nextMilestone.position - milestone.position);
        const followingMilestone = this.milestones[i + 2] || null;

        return { progress, nextMilestone, followingMilestone };
      }
    }

    // Default fallback (shouldn't really hit this unless something's wrong)
    return { progress: 0, nextMilestone: null, followingMilestone: null };
  }

  /**
   * Generates the progress message to show in the UI.
   * @param {number} totalPrice
   * @param {Object|null} nextMilestone
   * @param {Object|null} followingMilestone
   * @returns {string}
   */
  generateMessage(totalPrice, nextMilestone, followingMilestone) {
    if (!nextMilestone) return this.finalMessage;

    const spendRequired = (
      (nextMilestone.threshold - totalPrice) /
      100
    ).toFixed(2);
    let message = nextMilestone.heading.replace(
      '#difference',
      `$${spendRequired}`
    );

    if (followingMilestone) {
      const nextDifference = (
        (followingMilestone.threshold - totalPrice) /
        100
      ).toFixed(2);
      message = message.replace('#next_difference', `$${nextDifference}`);
    }

    return message;
  }

  /**
   * Updates the DOM with the given progress and message.
   * @param {number} progress
   * @param {string} message
   */
  updateUI(progress, message) {
    this.querySelector('.cart-milestones__heading').innerText = message;
    this.style.setProperty('--progress', `${progress}%`);
  }
}

customElements.define('cart-milestones', CartMilestones);
